import { meQueryOptions, useUserId } from "@/api/auth";
import {
  memberBeneficiaryQuery,
  myLocationQueryOptions,
  payoutDetailsForMemberQuery,
  useChangeEmailMutation,
  useMemberFeatures,
  useUpdateProfileMutation,
} from "@/api/me";
import {
  myNotificationsPreferencesQuery,
  myPushDevicesQuery,
  pushSubscriptionQuery,
  useRegisterDeviceForNotificationsMutation,
  useUnregisterDeviceForNotificationsMutation,
  useUpdateMyNotificationPreferencesMutation,
} from "@/api/myNotifications";
import { BeneficiaryDisplay } from "@/components/BeneficiaryDisplay";
import { PageInset } from "@/components/PageInset";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { ButtonLink } from "@/components/ui/Link";
import { useNotificationPermission } from "@/hooks/useServiceWorker";
import { seo } from "@/utils/seo";
import { Box, Field, Flex, Input, Label, QueryHandler, Switch, Text, toast } from "@givenwell/components";
import { ChevronRightIcon, WarningTriangleIcon } from "@givenwell/core-solid-icons";
import { GetUserInfoResponse } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ComponentProps, ReactNode, useId, useState } from "react";

export const Route = createFileRoute("/_auth/_app/settings/")({
  component: Settings,
  head: () => ({ meta: seo({ title: "Settings" }) }),
});

function Settings() {
  const meQuery = useQuery(meQueryOptions);
  const caps = useMemberFeatures();

  return (
    <>
      <QueryHandler
        query={meQuery}
        success={({ data: me }) => (
          <PageInset>
            <Box>
              <SettingsSection>
                <ProfileFormDialog me={me}>
                  <SettingsRow label="Name">
                    {me.given_name} {me.family_name}
                  </SettingsRow>
                </ProfileFormDialog>
                <ChangeEmailDialog>
                  <SettingsRow label="Email">{me.email}</SettingsRow>
                </ChangeEmailDialog>
              </SettingsSection>
              <SettingsSection>
                <PushStuff />
                <EmailStuff />
              </SettingsSection>
              {caps.hasClaims && !me.roles.includes("admin") && (
                <SettingsSection>
                  <ClaimsAccount />
                </SettingsSection>
              )}
            </Box>
          </PageInset>
        )}
      />
    </>
  );
}

function SettingsSection({ children }: { children?: ReactNode }) {
  return (
    <Box
      css={{
        "& + &": {
          borderTop: `1px solid ${colors.outline2}`,
          marginTop: 12,
          paddingTop: 12,
        },
      }}
    >
      {children}
    </Box>
  );
}

function SettingsRow({
  label,
  children,
  ...props
}: ComponentProps<"button"> & { label: string; children?: ReactNode }) {
  return (
    <Flex
      as="button"
      css={{ height: 48, alignItems: "center", fontScale: "md", overflow: "hidden", gap: 16, width: "100%" }}
      {...props}
    >
      <Text>{label}</Text>
      <Flex
        css={{
          flex: "1 0 0px",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
          color: colors.text2,
          overflow: "hidden",
        }}
      >
        <Text css={{ lineClamp: 1 }}>{children}</Text>
        <ChevronRightIcon />
      </Flex>
    </Flex>
  );
}

function ProfileFormDialog({ me, children }: { me: GetUserInfoResponse; children?: ReactNode }) {
  const mut = useUpdateProfileMutation();

  const [open, setOpen] = useState(false);
  const [givenName, setGivenName] = useState(me.given_name);
  const [familyName, setFamilyName] = useState(me.family_name);

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) {
      setGivenName(me.given_name);
      setFamilyName(me.family_name);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Change name"
      content={
        <form
          id="change-name-form"
          onSubmit={e => {
            e.preventDefault();
            mut.mutate(
              { given_name: givenName, family_name: familyName },
              {
                onSuccess() {
                  setOpen(false);
                  toast("Saved!", {
                    type: "success",
                  });
                },
              },
            );
          }}
        >
          <Box css={{ spaceY: 16 }}>
            <Field>
              <Label>First name</Label>
              <Input value={givenName} onValueChange={setGivenName} />
            </Field>
            <Field>
              <Label>Last name</Label>
              <Input value={familyName} onValueChange={setFamilyName} />
            </Field>
          </Box>
        </form>
      }
      footer={
        <>
          <Button variant="plain" color="neutral" disabled={mut.isPending} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" form="change-name-form" variant="filled" color="primary" loading={mut.isPending}>
            Save
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}

function ChangeEmailDialog({ children }: { children?: ReactNode }) {
  const mut = useChangeEmailMutation();

  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) {
      setNewEmail("");
      setCurrentPassword("");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Change email"
      content={
        <form
          id="change-email-form"
          onSubmit={e => {
            e.preventDefault();
            mut.mutate(
              { new_email_address: newEmail, current_password: currentPassword },
              {
                onSuccess() {
                  setOpen(false);
                },
              },
            );
          }}
        >
          <Box css={{ spaceY: 16 }}>
            <Field>
              <Label>New email</Label>
              <Input type="email" required value={newEmail} onValueChange={setNewEmail} />
            </Field>
            <Field>
              <Label>Current password</Label>
              <Input
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onValueChange={setCurrentPassword}
              />
            </Field>
            <Text css={{ fontScale: "xs", color: colors.text2 }}>
              We'll notify both your old and new email addresses about this change. If you have any issues, please
              contact <SupportLink href="mailto:support@givenwell.com">support@givenwell.com</SupportLink>.
            </Text>
          </Box>
        </form>
      }
      footer={
        <>
          <Button variant="plain" color="neutral" disabled={mut.isPending} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" form="change-email-form" variant="filled" color="primary" loading={mut.isPending}>
            Change email
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}

const SupportLink = styled("a", {
  color: colors.blue600,
  fontWeight: 500,
  textDecoration: "underline",
});

function PushStuff() {
  const userId = useUserId();
  const myDevices = useQuery({ ...myPushDevicesQuery(userId), enabled: !!userId });
  const mutation = useRegisterDeviceForNotificationsMutation();
  const permission = useNotificationPermission();
  const subscription = useQuery(pushSubscriptionQuery);

  const unsubMut = useUnregisterDeviceForNotificationsMutation();

  const hasNotifications =
    (permission === "granted" && myDevices.data?.includes(subscription.data?.endpoint || "")) || false;

  const id = useId();
  return (
    <div>
      <Flex css={{ items: "center", height: 48 }}>
        <Label htmlFor={id} css={{ flex: 1, fontScale: "md", fontWeight: 400 }}>
          Send push notifications
        </Label>
        <Switch
          id={id}
          checked={hasNotifications}
          indeterminate={unsubMut.isPending || mutation.isPending || myDevices.isFetching || subscription.isFetching}
          onCheckedChange={async checked => {
            if (checked) {
              mutation.mutate();
            } else {
              const endpoint = subscription.data?.endpoint;
              if (endpoint) {
                unsubMut.mutate({
                  endpoint: endpoint,
                });
              }
            }
          }}
        />
      </Flex>
    </div>
  );
}

function EmailStuff() {
  const preferencesQuery = useQuery(myNotificationsPreferencesQuery);
  const mutation = useUpdateMyNotificationPreferencesMutation();
  const id = useId();
  return (
    <Flex css={{ items: "center", height: 48 }}>
      <Label htmlFor={id} css={{ flex: 1, fontScale: "md", fontWeight: 400 }}>
        Send email notifications
      </Label>
      <Switch
        id={id}
        checked={preferencesQuery.data?.email_notifications_enabled}
        indeterminate={preferencesQuery.isFetching || mutation.isPending}
        onCheckedChange={checked => {
          mutation.mutate({ email_notifications_enabled: checked });
        }}
      />
    </Flex>
  );
}

function ClaimsAccount() {
  const { data: settings } = useSuspenseQuery(payoutDetailsForMemberQuery);
  const beneficiaryQuery = useQuery(memberBeneficiaryQuery);
  const { data: beneficiary } = beneficiaryQuery;
  const { data: location } = useSuspenseQuery(myLocationQueryOptions);
  const showCountry = location?.country_code && beneficiary?.country && location.country_code !== beneficiary.country;

  return (
    <Box css={{ spaceY: 0 }}>
      <Flex css={{ height: 48, alignItems: "center", justifyContent: "space-between" }}>
        <Text css={{ fontScale: "md", weight: 400 }}>Bank Account</Text>
        <Box css={{ mr: -16 }}>
          {settings.beneficiary_id ? (
            <ButtonLink to="/claims/setup" variant="plain" color="primary" rightIcon={<ChevronRightIcon />}>
              Change
            </ButtonLink>
          ) : (
            <ButtonLink to="/claims/setup" variant="plain" color="primary" rightIcon={<ChevronRightIcon />}>
              Set up
            </ButtonLink>
          )}
        </Box>
      </Flex>
      {settings.beneficiary_id ? (
        <Box css={{ spaceY: 4 }}>
          <BeneficiaryDisplay
            beneficiary={beneficiary}
            showCountry={!!showCountry}
            loading={beneficiaryQuery.isPending}
          />
          <Text css={{ fontScale: "sm", color: colors.text2 }}>
            Approved claims will be paid into this bank account.
          </Text>
        </Box>
      ) : (
        <Box css={{ spaceY: 16 }}>
          <Box css={{ background: colors.yellow, p: 12, rounded: 16, color: colors.yellow900, weight: 500 }}>
            <Flex css={{ alignItems: "flex-start", gap: 8 }}>
              <WarningTriangleIcon color={colors.yellow800} />
              <Text css={{ fontScale: "sm" }}>You have not set up a bank account for claims payment</Text>
            </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
}
